import { Injectable } from '@angular/core';
import { Sort } from '@angular/material/sort';
import { forkJoin, of, Subject, Subscription } from 'rxjs';
import { ApiService } from 'core/api/api.service';
import { LoadingState } from 'core/common-entities/loading-state.enum';
import { ExportUtilsService } from 'core/export-utils/export-utils.service';
import { TrackingInfo } from 'src/app/modules/crm/dashboard/entities/tracking-info';
import { StorageValues } from '../../../shared/entities/storage-values.enum';
import { AdvancedSearchRequestVars, AdvancedSearchService } from '../../services/advanced-search.service';
import { InvoiceResponse } from '../entities/invoice-entity';
import { InvoiceHeader } from '../entities/invoice-header-entity';
import { InvoiceSortColumn, InvoiceSortDirection } from '../entities/invoice-sort.enum';

@Injectable({
  providedIn: 'root'
})
export class InvoicesService {
  private urls = {
    invoicesListUrl: '/api/invoice/list/',
    invoiceDetailsUrl: '/api/invoice/items/',
    invoiceHeaderUrl: '/api/invoice/header/',
    invoiceFooterUrl: '/api/invoice/footer/',
    invoicePrintUrl: '/api/invoice/printInvoice/',
    templateValuesUrl: '/api/invoice/templatevalues',
    trackingNumberList: '/api/invoice/trackingNumbers',
    googleTrackUrl: 'https://google.com/search?q={trackingNumber}+{carrier}'
  };
  private defaultSort = {
    direction: 'desc',
    sortBy: 'invoiceDate'
  };
  private listPayload = {
    date: {
      startDate: null,
      endDate: null
    },
    nameFilter: {
      employeeId: '',
      bpNumber: '',
      customerName: '',
      invoiceNumber: '',
      dollarAmount: '',
      poNumber: '',
      partNumber: '',
      displayedSearchValue: '',
      partialSearch: true
    },
    sort: {
      direction: InvoiceSortDirection[this.defaultSort.direction],
      sortBy: InvoiceSortColumn[this.defaultSort.sortBy]
    },
    page: 1
  };
  private invoiceObject = {
    url: this.urls.invoicesListUrl,
    payload: this.listPayload
  };
  invoicesList$ = new Subject<any>();
  invoicesCount$ = new Subject<any>();
  private invoicesResultCount = {
    exact: 0,
    partial: 0
  };
  templateValues = {
    InvoiceSortType: null,
    SortDirection: null
  };
  pageSize = 15;
  isProcessing$ = new Subject<LoadingState>();
  private pendingRequests = new Subscription();

  constructor(
    private apiService: ApiService,
    private advancedSearchService: AdvancedSearchService,
    private exportUtilsService: ExportUtilsService
  ) { }

  getInvoiceList(invoicesPayload?, sort?: Sort, shouldCache?: boolean) {
    if (invoicesPayload) {
      if (shouldCache) {
        sessionStorage.setItem(StorageValues.CachedFilters + StorageValues.InvoiceKey,
          JSON.stringify(invoicesPayload));
      }

      this.listPayload.date = {
        startDate: invoicesPayload.startDate,
        endDate: invoicesPayload.endDate
      };
      this.listPayload.nameFilter = {
        employeeId: invoicesPayload.accountManager ? invoicesPayload.accountManager.employeeId : null,
        bpNumber: invoicesPayload.bpNumber,
        customerName: invoicesPayload.customerName,
        invoiceNumber: invoicesPayload.invoiceNumber,
        dollarAmount: invoicesPayload.dollarAmount?.toString(),
        poNumber: invoicesPayload.poNumber,
        partNumber: invoicesPayload.partNumber,
        displayedSearchValue: invoicesPayload.displayedSearchValue,
        partialSearch: true
      };
      this.resetSort();
    }
    if (sort) {
      this.listPayload.sort = {
        direction: InvoiceSortDirection[sort.direction],
        sortBy: InvoiceSortColumn[sort.active]
      };
      sessionStorage.setItem(StorageValues.CachedSort + StorageValues.InvoiceKey, JSON.stringify(sort));
    }
    this.updatePage(1);
  }

  resetSort() {
    this.invoiceObject.payload.sort = {
      direction: InvoiceSortDirection[this.defaultSort.direction],
      sortBy: InvoiceSortColumn[this.defaultSort.sortBy]
    };
  }

  updatePage(pageIndex) {
    this.isProcessing$.next(LoadingState.loading);
    const requestVars: AdvancedSearchRequestVars = {
      recordCount$: this.invoicesCount$,
      recordList$: this.invoicesList$,
      resultCount: this.invoicesResultCount
    };

    this.invoiceObject.payload.page = pageIndex;

    this.getResultsForRequest(this.invoiceObject, requestVars);
  }

  private getResultsForRequest(requestObj, requestVars) {
    // cancel existing requests (if any), and reinitialize the subscription
    this.pendingRequests.unsubscribe();
    this.pendingRequests = new Subscription();

    const offsets = this.advancedSearchService.getOffsetsForPage(requestObj.payload, requestVars, this.pageSize);

    const requestObs = this.advancedSearchService.getRequestObservables<InvoiceResponse>(InvoiceResponse, requestObj, offsets);

    const exactCall = requestObs.exact.subscribe((exactResultObj: InvoiceResponse) => {
      const parsedExactObj = exactResultObj;
      requestVars.recordList$.next(parsedExactObj.invoices);
      requestVars.recordCount$.next(parsedExactObj.count);
      this.isProcessing$.next(LoadingState.partiallyLoaded);
      // only update the count on the first page of results. This prevents total page issues when the object is generated.
      if (offsets.exactOffset === 0) {
        this.invoicesResultCount.exact = exactResultObj.count;
      }
      const partialCall = requestObs.partial.subscribe((partialResultObj: InvoiceResponse) => {
        const parsedPartialObj = partialResultObj;
        requestVars.recordList$.next(parsedExactObj.invoices.concat(parsedPartialObj.invoices).slice(0, this.pageSize));
        // only update the count on the first page of results. This prevents total page issues when the object is generated.
        if (offsets.partialOffset === 0) {
          this.invoicesResultCount.partial = partialResultObj.count;
        }
        requestVars.recordCount$.next(this.invoicesResultCount.exact + this.invoicesResultCount.partial);
        this.isProcessing$.next(LoadingState.fullyLoaded);
      });
      this.pendingRequests.add(partialCall); // store call to be able to cancel it later
    });
    this.pendingRequests.add(exactCall); // store call to be able to cancel it later
  }

  getTemplateValues(callback: (...args: any[]) => any) {
    const cachedData = this.templateValues.InvoiceSortType &&
      this.templateValues.SortDirection;
    const observableList = [];

    if (cachedData) {
      observableList.push(
        of({
          InvoiceSortType: this.templateValues.InvoiceSortType,
          SortDirection: this.templateValues.SortDirection
        })
      );
    } else {
      observableList.push(this.apiService.get(this.urls.templateValuesUrl));
    }

    forkJoin(observableList).subscribe(resultArray => {
      const results: any = resultArray[0];
      this.templateValues.InvoiceSortType = results.InvoiceSortType;
      this.templateValues.SortDirection = results.SortDirection;

      callback(this.templateValues);
    });
  }

  getInvoiceHeader(invoiceNumber: string, callback: (...args: any[]) => any) {
    this.apiService.get<InvoiceHeader>(this.urls.invoiceHeaderUrl + invoiceNumber).subscribe(result => callback(result));
  }

  getInvoiceFooter(invoiceNumber: string, callback: (...args: any[]) => any) {
    this.apiService.get(this.urls.invoiceFooterUrl + invoiceNumber).subscribe(result => callback(result));
  }

  getInvoiceDetails(params, callback: (...args: any[]) => any) {
    this.apiService.post(this.urls.invoiceDetailsUrl, params).subscribe(result => callback(result));
  }

  getInvoiceTrackingNumberList(orderNumber) {
    return this.apiService.get(`${this.urls.trackingNumberList}/${orderNumber}`);
  }

  trackOrder(trackingDetails: TrackingInfo) {
    let searchUrl = this.urls.googleTrackUrl.replace('{trackingNumber}', trackingDetails.trackingNumber);
    searchUrl = searchUrl.replace('{carrier}', trackingDetails.carrier);
    window.open(searchUrl, '_blank');
  }

  /**
   * Prints an invoice
   * @param invoiceNumber - The invoice number to be printed
   */
  printInvoice(invoiceNumber: string) {
    const printWindow = this.exportUtilsService.openPrintFile();
    this.apiService.getBlob(this.urls.invoicePrintUrl + invoiceNumber)
      .subscribe((data: BlobPart) => this.exportUtilsService.printFile(data, printWindow));
  }
}
